import clsx from 'clsx';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import ToggleIcon from '../../assets/icons/sidebar-toggle-icon.svg?react';
import { toggleSwitch } from '../../reducers/switch';
import Button from '../../components/Button';
import EditIcon from '../../assets/icons/edit-icon.svg?react';
import { toggleModal } from '../../reducers/modalToggle';
import { LoginResponseData } from '@EHSNavigator/backend';
import { useLocalStorage } from '@uidotdev/usehooks';
import moment from 'moment';

interface GeneralSettingsProps {
  otherClasses?: string;
}

const GeneralSettings: React.FC<GeneralSettingsProps> = ({ otherClasses }) => {
  const generalSettingsClasses = clsx(otherClasses);
  const dispatch = useDispatch();
  const [userInfo] = useLocalStorage<LoginResponseData | null>(
    'userInfo',
    null,
  );
  const [newEmail] = useLocalStorage<string | null>('newEmail', null);
  const [message, setMessage] = useLocalStorage<string | null>('message', null);

  function getTimeAgo(date: string) {
    const time = moment(date);
    const now = moment();
    const diffInMinutes = now.diff(time, 'minutes');
    const diffInHours = now.diff(time, 'hours');
    const diffInDays = now.diff(time, 'days');
    if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
    } else {
      return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
    }
  }

  useEffect(() => {
    if (message) {
      setTimeout(() => {
        setMessage(null);
      }, 10000);
    }
  }, [message]);

  return (
    <div className={generalSettingsClasses} data-testid="general-settings">
      <div className="border-neutral-tones-text/35 border-b pb-6 lg:pb-12">
        <div className="flex w-full max-w-818 items-start gap-2.5">
          <button
            className="bg-translucent-background/80 border-neutral-tones-text/30 rounded-5 cursor-pointer border p-0.5 lg:hidden"
            onClick={() => dispatch(toggleSwitch())}
          >
            <ToggleIcon />
          </button>
          <div className="w-full">
            <h1 className="h3">General Settings</h1>
            <p className="font-inter-variable text-neutral-tones-text/80 -tracking-button mt-15 hidden text-xl leading-6 font-medium lg:block">
              View and edit your account details.
            </p>
          </div>
        </div>
        {newEmail && (
          <p className="text-inter-variable text-brand-byzantine bg-indicator-blue mx-auto mt-6 w-fit rounded px-1 py-1 text-center text-sm font-medium">
            Verification email has been sent to your {newEmail} email address.
          </p>
        )}
        {message && (
          <p className="text-inter-variable text-brand-byzantine bg-indicator-blue mx-auto mt-6 w-fit rounded px-1 py-1 text-center text-sm font-medium">
            {message}
          </p>
        )}
      </div>
      <div className="border-neutral-tones-text/35 w-full border-b py-6 lg:py-12">
        <div className="mb-6 flex items-center justify-between">
          <h4 className="text-brand-byzantine">Personal Details</h4>
          <Button
            size="smaller"
            otherClasses="w-121"
            onClick={() => dispatch(toggleModal('edit-personal-details'))}
          >
            <EditIcon className="h-5 w-5 [&>path]:fill-current" /> Edit
          </Button>
        </div>
        <div className="mt-6 grid grid-cols-2 justify-between gap-x-9 gap-y-6 sm:grid-cols-3 xl:grid-cols-5">
          <div className="flex flex-col gap-2.5">
            <h6 className="font-inter-variable text-neutral-tones-text/80 text-base leading-5 font-bold lg:text-lg">
              Employee ID
            </h6>
            <p className="font-inter-variable text-neutral-tones-text text-base leading-5 font-normal uppercase">
              {userInfo?.user?.employeeId ? userInfo?.user?.employeeId : '---'}
            </p>
          </div>
          <div className="flex flex-col gap-2.5">
            <h6 className="font-inter-variable text-neutral-tones-text/80 text-base leading-5 font-bold lg:text-lg">
              Designation
            </h6>
            <p className="font-inter-variable text-neutral-tones-text text-base leading-5 font-normal capitalize">
              {userInfo?.user?.jobTitle ? userInfo?.user?.jobTitle : '---'}
            </p>
          </div>
          <div className="flex flex-col gap-2.5">
            <h6 className="font-inter-variable text-neutral-tones-text/80 text-base leading-5 font-bold lg:text-lg">
              Name
            </h6>
            <p className="font-inter-variable text-neutral-tones-text text-base leading-5 font-normal capitalize">
              {userInfo?.user?.fullName}
            </p>
          </div>
          <div className="flex flex-col gap-2.5">
            <h6 className="font-inter-variable text-neutral-tones-text/80 text-base leading-5 font-bold lg:text-lg">
              Email Address
            </h6>
            <p className="font-inter-variable text-neutral-tones-text truncate text-base leading-5 font-normal">
              {userInfo?.user?.email}
            </p>
          </div>
          <div className="flex flex-col gap-2.5">
            <h6 className="font-inter-variable text-neutral-tones-text/80 text-base leading-5 font-bold lg:text-lg">
              Contact Number
            </h6>
            <p className="font-inter-variable text-neutral-tones-text text-base leading-5 font-normal">
              {userInfo?.user?.contact || '---'}
            </p>
          </div>
          <div className="flex flex-col gap-2.5">
            <h6 className="font-inter-variable text-neutral-tones-text/80 text-base leading-5 font-bold lg:text-lg">
              Date Of Birth
            </h6>
            <p className="font-inter-variable text-neutral-tones-text text-base leading-5 font-normal">
              {userInfo?.user?.dob && moment(userInfo?.user?.dob).isValid()
                ? moment(userInfo?.user?.dob).format('MMMM DD, YYYY')
                : '---'}
            </p>
          </div>
          <div className="flex flex-col gap-2.5">
            <h6 className="font-inter-variable text-neutral-tones-text/80 text-base leading-5 font-bold lg:text-lg">
              Role
            </h6>
            <p className="font-inter-variable text-neutral-tones-text text-base leading-5 font-normal">
              {userInfo?.user?.role?.name}
            </p>
          </div>
        </div>
      </div>
      <div className="border-neutral-tones-text/35 w-full border-b py-6 lg:py-12">
        <div className="mb-6 flex items-center justify-between">
          <h4 className="text-brand-byzantine">Password</h4>
          <Button
            onClick={() => dispatch(toggleModal('change-password'))}
            size="smaller"
            otherClasses="w-121"
          >
            <EditIcon className="h-5 w-5 [&>path]:fill-current" /> Edit
          </Button>
        </div>

        {userInfo?.user?.setPasswordAt ? (
          <p className="font-inter-variable text-neutral-tones-text text-base leading-5 font-normal italic lg:text-lg">
            Your Password was changed{' '}
            {getTimeAgo(userInfo?.user?.setPasswordAt as string)}. Never share
            your password with anyone!
          </p>
        ) : (
          <p className="font-inter-variable text-neutral-tones-text text-base leading-5 font-normal italic lg:text-lg">
            Your Password was never changed. Never share your password with
            anyone!
          </p>
        )}
      </div>

      {/* <div className="border-neutral-tones-text/35 w-full border-b py-6 lg:py-8">
        <div className="mb-6 flex items-center justify-between">
          <h4 className="text-brand-posh-pink">Delete an account</h4>
          <Button
            onClick={() => dispatch(toggleModal('delete-account'))}
            size="smaller"
            otherClasses="whitespace-nowrap bg-brand-posh-pink hover:text-brand-posh-pink hover:border-brand-posh-pink"
          >
            <TrashIcon className="hidden h-5 w-5 sm:block [&>path]:stroke-current" />
            Delete Your Account
          </Button>
        </div>
        <p className="font-inter-variable text-neutral-tones-text text-base leading-5 font-normal italic lg:text-lg">
          Once you delete your account, there is no going back. Please be
          certain.
        </p>
      </div> */}
    </div>
  );
};

export default GeneralSettings;
